/**
 * Microsoft Clarity observability layer (core).
 *
 * One small, typed, side-effect-safe wrapper around `window.clarity(...)`. Nothing else in
 * the app should call `window.clarity` directly — import from `@/lib/observability` instead.
 *
 * Design rules:
 *  - Telemetry never throws. Every public function is a no-op when Clarity is disabled and
 *    swallows its own errors.
 *  - Disabled by default outside production. In prod it auto-enables when a project id is set.
 *  - Never sends PII. Values are sanitized (emails / JWT-like tokens redacted, truncated) and
 *    the taxonomy is finite-cardinality (buckets, enums, normalized routes — never raw ids).
 *
 * See docs/observability/microsoft-clarity-integration.md and clarity-event-taxonomy.md.
 */

import type { ClarityEvent } from './events';
import { enqueueBehavioralEvent } from './sink';

// ── Clarity global typing ────────────────────────────────────────────────────
type ClarityCommand =
  | ['event', string]
  | ['set', string, string | string[]]
  | ['identify', string, string?, string?, string?]
  | ['consentv2', ClarityConsentV2]
  | ['consent', boolean?]
  | ['upgrade', string];

export interface ClarityFn {
  (...args: ClarityCommand): void;
  q?: unknown[];
}

type ClarityWindow = Window & { clarity?: ClarityFn };

export interface ClarityConsentV2 {
  ad_Storage: 'granted' | 'denied';
  analytics_Storage: 'granted' | 'denied';
}

// ── Environment (Vite inlines these statically at build time) ────────────────
const ENV = {
  projectId: import.meta.env.VITE_CLARITY_PROJECT_ID,
  enableFlag: import.meta.env.VITE_ENABLE_CLARITY,
  identify: import.meta.env.VITE_CLARITY_IDENTIFY === 'true',
  appEnv: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
  appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
  releaseSha: import.meta.env.VITE_RELEASE_SHA || 'unknown',
  mode: import.meta.env.MODE,
  prod: import.meta.env.PROD,
} as const;

export const APP_NAME = 'bizcore-web';

/**
 * Runtime opt-in for verification (E2E) only. Honored ONLY in non-production builds so it can
 * never be flipped on in a shipped prod bundle. The E2E spec sets this in localStorage and
 * stubs `window.clarity`; all other specs leave it unset, so Clarity stays inert.
 */
const CLARITY_TEST_FLAG = '__bizcore_clarity_force__';
function runtimeTestEnabled(): boolean {
  // Never honored in a REAL production build (MODE==='production'), so it can't be flipped on
  // in a shipped prod bundle. Note: `vite build --mode test` sets PROD=true but MODE='test'.
  if (ENV.mode === 'production') return false;
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(CLARITY_TEST_FLAG) === '1';
  } catch {
    return false;
  }
}

let cachedEnabled: boolean | undefined;

/** Whether Clarity should run in this environment. Cached after first evaluation. */
export function isClarityEnabled(): boolean {
  if (cachedEnabled !== undefined) return cachedEnabled;
  cachedEnabled = computeEnabled();
  return cachedEnabled;
}

function computeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (ENV.enableFlag === 'false') return false; // hard kill-switch, any env
  // Gate on MODE, not PROD: `vite build --mode test` reports PROD=true but is NOT a real
  // production deploy, so PROD would wrongly enable/disable the E2E build.
  if (ENV.mode === 'production') return Boolean(ENV.projectId); // real prod: on when configured
  if (ENV.mode === 'test') return runtimeTestEnabled(); // E2E build: only via the stub flag
  // development / staging / preview: off unless explicitly opted in.
  if (ENV.enableFlag === 'true' && Boolean(ENV.projectId)) return true;
  return runtimeTestEnabled();
}

/** Test-only: reset the memoized enabled flag (used by unit/E2E setup). */
export function __resetClarityEnabledCache(): void {
  cachedEnabled = undefined;
}

// ── Low-level transport ──────────────────────────────────────────────────────
let initialized = false;

function clarityWindow(): ClarityWindow {
  return window as ClarityWindow;
}

/** Define the command queue so calls made before the remote tag loads are buffered. */
function ensureClarityQueue(): void {
  const w = clarityWindow();
  if (typeof w.clarity === 'function') return; // real snippet or a test stub already present
  const fn: ClarityFn = function (...args: unknown[]) {
    (fn.q = fn.q || []).push(args);
  } as ClarityFn;
  w.clarity = fn;
}

function send(...args: ClarityCommand): void {
  if (!isClarityEnabled()) return;
  try {
    clarityWindow().clarity?.(...args);
  } catch {
    /* telemetry must never break the app */
  }
}

// ── Init ─────────────────────────────────────────────────────────────────────
/**
 * Initialize Clarity exactly once. Safe to call in any environment: a no-op when disabled.
 * Injects the official tag in real (non-test) builds; in the E2E/stub build it only ensures
 * the command queue so a stubbed `window.clarity` collects calls without any network.
 */
export function initClarity(): void {
  if (initialized || !isClarityEnabled()) return;
  initialized = true;

  ensureClarityQueue();

  // Never load the remote tag in the E2E build (no real network in CI).
  if (ENV.mode !== 'test' && ENV.projectId) {
    injectClarityScript(ENV.projectId);
  }

  // Static, finite-cardinality context for every session.
  setClarityTags({
    app_name: APP_NAME,
    app_env: ENV.appEnv,
    app_version: ENV.appVersion,
    release_sha: ENV.releaseSha,
  });

  installGlobalErrorListeners();
}

function injectClarityScript(projectId: string): void {
  if (document.getElementById('ms-clarity-tag')) return;
  const script = document.createElement('script');
  script.id = 'ms-clarity-tag';
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
  document.head.appendChild(script);
}

let globalListenersInstalled = false;
function installGlobalErrorListeners(): void {
  if (globalListenersInstalled || typeof window === 'undefined') return;
  globalListenersInstalled = true;

  window.addEventListener('error', (e) => {
    const msg = e?.message ?? '';
    if (isChunkLoadError(msg)) {
      trackClarityEvent('chunk-load-failed', { error_area: 'lazy-chunk' });
    } else {
      trackClarityEvent('uncaught-error', { error_area: 'window' });
    }
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e?.reason as { message?: string } | undefined)?.message ?? '';
    if (isChunkLoadError(reason)) {
      trackClarityEvent('chunk-load-failed', { error_area: 'dynamic-import' });
    } else {
      trackClarityEvent('unhandled-promise-rejection', { error_area: 'promise' });
    }
  });
}

function isChunkLoadError(message: string): boolean {
  return /loading chunk|dynamically imported module|failed to fetch dynamically/i.test(message);
}

// ── Value sanitization (defense-in-depth against accidental PII) ──────────────
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const JWT_RE = /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
const LONG_TOKEN_RE = /\b[A-Za-z0-9_-]{40,}\b/g;
const MAX_TAG_LEN = 120;

/** Scrub a tag/metadata value: redact emails + token-like strings, then truncate. */
export function sanitizeTagValue(value: string | number | boolean): string {
  let s = String(value);
  s = s.replace(EMAIL_RE, '[redacted-email]');
  s = s.replace(JWT_RE, '[redacted-token]');
  s = s.replace(LONG_TOKEN_RE, '[redacted]');
  if (s.length > MAX_TAG_LEN) s = `${s.slice(0, MAX_TAG_LEN)}…`;
  return s;
}

// ── Tags ─────────────────────────────────────────────────────────────────────
export type ClarityTagValue = string | number | boolean | null | undefined;
export type ClarityTags = Record<string, ClarityTagValue>;

/** Set one or more filterable session tags. null/undefined values are skipped. */
export function setClarityTags(tags: ClarityTags): void {
  if (!isClarityEnabled()) return;
  for (const [key, value] of Object.entries(tags)) {
    if (value === null || value === undefined || value === '') continue;
    send('set', key, sanitizeTagValue(value));
  }
}

// ── Events ───────────────────────────────────────────────────────────────────
/**
 * Fire a custom Clarity event (usable in Recordings, Dashboard filters, Smart Events, Funnels).
 * Optional metadata is attached as filterable tags first — keep its keys finite-cardinality.
 */
export function trackClarityEvent(eventName: ClarityEvent, metadata?: ClarityTags): void {
  // Fan out to the first-party telemetry sink FIRST, independently of Clarity's own gate, so the
  // Growth Intelligence Engine receives every event even when Clarity is disabled. Never throws.
  enqueueBehavioralEvent(eventName, metadata);
  if (!isClarityEnabled()) return;
  if (metadata) setClarityTags(metadata);
  send('event', eventName);
}

// ── Consent ──────────────────────────────────────────────────────────────────
export interface ConsentState {
  analytics: boolean;
  ad?: boolean;
}

/** Wire Clarity Consent V2 to your consent state. Call when consent is known / changes. */
export function setClarityConsent(consent: ConsentState): void {
  send('consentv2', {
    analytics_Storage: consent.analytics ? 'granted' : 'denied',
    ad_Storage: consent.ad ? 'granted' : 'denied',
  });
}

// ── Identify (opt-in, opaque ids only) ───────────────────────────────────────
export interface ClarityUserContext {
  /** Opaque, non-PII, stable id (e.g. user GUID). Never an email/phone/raw name. */
  customId: string;
  /** Optional non-PII friendly label (e.g. a role name). Never PII. */
  friendlyName?: string;
}

/**
 * Associate the session with an opaque support id. No-op unless VITE_CLARITY_IDENTIFY=true.
 * Deliberately conservative: identify is OFF by default so a session is never linked to a
 * person without an explicit, reviewed decision.
 */
export function identifyClarityUser(ctx: ClarityUserContext): void {
  if (!ENV.identify) return;
  if (!ctx.customId) return;
  // customId is hard-sanitized too, in case a caller passes the wrong field.
  send('identify', sanitizeTagValue(ctx.customId), undefined, undefined, ctx.friendlyName);
}

// ── Masking ──────────────────────────────────────────────────────────────────
/**
 * Apply `data-clarity-mask="true"` to sensitive regions that may be rendered dynamically
 * (lazy routes, modals). Idempotent; safe to call on every route change. This complements
 * Clarity's default input masking and the explicit `data-clarity-mask` attributes in JSX.
 */
const SENSITIVE_SELECTORS = [
  'input[type="password"]',
  'input[autocomplete="one-time-code"]',
  '[data-sensitive="true"]',
  '[data-testid$="-password"]',
  '[data-testid*="pan"]',
  '[data-testid*="vat"]',
  '[data-testid*="payslip"]',
];

export function maskSensitiveClarityRegions(root: ParentNode = document): void {
  if (!isClarityEnabled()) return;
  try {
    root.querySelectorAll(SENSITIVE_SELECTORS.join(',')).forEach((el) => {
      el.setAttribute('data-clarity-mask', 'true');
    });
  } catch {
    /* never throw from telemetry */
  }
}
